import { useEffect, useRef, useState } from "react";

export type CameraStatus =
  | { kind: "starting" }
  | { kind: "scanning" }
  | { kind: "decoded" }
  | { kind: "error"; message: string };

/**
 * Camera + jsQR loop shared by the verify and invite scanners. Requests a high
 * resolution (invite QR codes are dense), throttles decoding to ~7 fps and
 * stops the camera as soon as `onDecode` accepts a payload (returns true).
 */
export function useQrCamera(onDecode: (text: string) => boolean, round: number) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<CameraStatus>({ kind: "starting" });
  const onDecodeRef = useRef(onDecode);
  onDecodeRef.current = onDecode;

  useEffect(() => {
    let raf = 0;
    let stream: MediaStream | null = null;
    let cancelled = false;
    let done = false;
    let lastScan = 0;

    const stop = () => {
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
    };

    (async () => {
      setStatus({ kind: "starting" });
      if (!window.isSecureContext) {
        setStatus({ kind: "error", message: "The camera needs a secure (https) connection." });
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus({ kind: "error", message: "This browser can't use the camera." });
        return;
      }
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
      } catch {
        setStatus({ kind: "error", message: "Camera unavailable or permission denied. Allow camera access and try again." });
        return;
      }
      if (cancelled) return stop();
      const video = videoRef.current;
      if (!video) return stop();
      video.srcObject = stream;
      video.muted = true;
      video.setAttribute("playsinline", "");
      await video.play().catch(() => {});
      const jsQR = (await import("jsqr")).default;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      setStatus({ kind: "scanning" });

      const tick = (ts: number) => {
        if (cancelled || done) return;
        raf = requestAnimationFrame(tick);
        if (!ctx || !video.videoWidth || ts - lastScan < 140) return;
        lastScan = ts;
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        ctx.drawImage(video, 0, 0);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "attemptBoth" });
        if (!code?.data) return;
        if (onDecodeRef.current(code.data)) {
          done = true;
          stop();
          setStatus({ kind: "decoded" });
        }
      };
      raf = requestAnimationFrame(tick);
    })();

    return () => {
      cancelled = true;
      stop();
    };
  }, [round]);

  return { videoRef, status };
}
